import { useRef, useState, VoidFunctionComponent } from 'react';
import { useFrame } from '@react-three/fiber';
import { Group, Mesh, MeshPhysicalMaterial, Shape, ShapeGeometry } from 'three';
import { Text } from '@react-three/drei';
import { celophane, garamondNarrowBook } from '@/lib/fonts';
import { legendaryCard, offblack } from '@/lib/colors';
import { CardBounds } from '@/lib/types';
import { useScratchedMetalRoughness } from '@/hooks/useScratchedMetalRoughness';

const calculateMeshWidth = (mesh: Mesh): number => {
    const { boundingBox } = mesh.geometry;
    return boundingBox ? boundingBox.max.x - boundingBox.min.x : 0;
};

const BOX_HEIGHT = 0.178;
const VALUE_LEFT_MARGIN = 0.026;

const threeMesh = new Mesh();
const threeGroup = new Group();

const drawCurvedBox = (boxWidth: number, leftX: number): Shape => {
    const shape = new Shape();
    const radius = BOX_HEIGHT / 2;
    const left = -boxWidth / 2 - leftX;
    const right = boxWidth / 2 - leftX;
    const bottom = -BOX_HEIGHT / 2;
    const top = BOX_HEIGHT / 2;
    shape.moveTo(left, top);
    shape.absarc(left, 0, radius, Math.PI / 2, (3 * Math.PI) / 2, false);
    shape.lineTo(right, bottom);
    shape.absarc(right, 0, radius, (3 * Math.PI) / 2, Math.PI / 2, false);
    return shape;
};

interface Props {
    label: string;
    value: string;
    width: number;
    cardBounds: CardBounds;
    legendary?: boolean;
}

export const StatusField: VoidFunctionComponent<Props> = ({ cardBounds, legendary, width, label, value }) => {
    const labelRef = useRef<Mesh>(threeMesh);
    const valueRef = useRef<Mesh>(threeMesh);
    const groupRef = useRef<Group>(threeGroup);
    const [bgShape, setBgShape] = useState<Shape>();
    const drawBox = useRef<boolean>(true);
    const geometryRef = useRef<ShapeGeometry>(new ShapeGeometry());
    const materialRef = useRef<MeshPhysicalMaterial>(new MeshPhysicalMaterial());
    const meshRef = useRef<Mesh>(new Mesh());
    useScratchedMetalRoughness(meshRef, materialRef, geometryRef, cardBounds, !!bgShape);

    useFrame(() => {
        if (
            drawBox.current &&
            valueRef.current.geometry.boundingBox &&
            valueRef.current.geometry.boundingBox.min.x < width
        ) {
            const labelWidth = calculateMeshWidth(labelRef.current);
            const valueWidth = calculateMeshWidth(valueRef.current);
            const textWidth = labelWidth + valueWidth + VALUE_LEFT_MARGIN;
            const leftX = -textWidth / 2;
            groupRef.current.position.x = leftX;
            valueRef.current.position.x = labelWidth + VALUE_LEFT_MARGIN;
            setBgShape(drawCurvedBox(textWidth, leftX));
            drawBox.current = false;
        }
    });

    return (
        <group ref={groupRef} position={[0, 0, 0]} scale={1.05}>
            <mesh ref={meshRef} visible={!!bgShape} position={[0, 0.014, 0]}>
                <shapeGeometry ref={geometryRef} attach="geometry" args={[bgShape]} />
                {legendary ? (
                    <meshPhysicalMaterial ref={materialRef} color={legendaryCard} metalness={1} transparent />
                ) : (
                    <meshBasicMaterial color="white" />
                )}
            </mesh>
            <group position={[0, 0.006, 0.01]}>
                <Text
                    ref={labelRef}
                    position={[0, 0.006, 0]}
                    font={celophane}
                    fontSize={0.037}
                    letterSpacing={-0.035}
                    anchorX="left"
                    color={offblack}
                >
                    {label}:
                </Text>
                <Text
                    ref={valueRef}
                    position={[0, 0, 0]}
                    anchorX="left"
                    font={garamondNarrowBook}
                    fontSize={0.102}
                    letterSpacing={-0.05}
                    color={offblack}
                    renderOrder={1}
                >
                    {value}
                </Text>
            </group>
        </group>
    );
};
